import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { FolderCog, Plus, Edit, Trash2 } from 'lucide-react';
import { DataTable } from '../../components/ui/data-table';

const EquipmentCategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/equipment-categories');
      setCategories(response.data);
    } catch (error) {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editItem) {
        await api.put(`/equipment-categories/${editItem.id}`, formData);
        toast.success('Categorie actualizata!');
      } else {
        await api.post('/equipment-categories', formData);
        toast.success('Categorie creata!');
      }
      setShowDialog(false);
      setEditItem(null);
      setFormData({ name: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvare');
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({ name: item.name, description: item.description || '' });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur doriti sa stergeti?')) return;
    try {
      await api.delete(`/equipment-categories/${id}`);
      toast.success('Categorie stearsa!');
      fetchData();
    } catch (error) {
      toast.error('Eroare la stergere');
    }
  };

  const tableColumns = [
    {
      key: 'name', label: 'Categorie Echipament', sortable: true, filterable: true,
      render: (value) => (
        <div className="flex items-center gap-2">
          <FolderCog className="w-4 h-4 text-blue-500" />
          <span className="font-medium">{value}</span>
        </div>
      )
    },
    { key: 'description', label: 'Descriere', sortable: false, filterable: true,
      render: (value) => <span className="text-slate-500 truncate max-w-[400px] block">{value || '-'}</span>
    },
    {
      key: 'actions', label: 'Actiuni', sortable: false, filterable: false, width: 'w-24',
      render: (_, row) => (
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          <Button variant="ghost" size="sm" onClick={() => handleEdit(row)}><Edit className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(row.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6" data-testid="equipment-categories-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900">Categorii Echipamente</h1>
          <p className="text-slate-600 mt-1">Nomenclator categorii echipamente ({categories.length} total)</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) { setEditItem(null); setFormData({ name: '', description: '' }); } }}>
          <DialogTrigger asChild>
            <Button className="btn-primary h-12"><Plus className="w-5 h-5 mr-2" />CATEGORIE NOUA</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>{editItem ? 'Editeaza Categorie' : 'Adauga Categorie'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Denumire *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="h-11" />
              </div>
              <div className="space-y-2">
                <Label>Descriere</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Anuleaza</Button>
                <Button type="submit" className="btn-primary">{editItem ? 'ACTUALIZEAZA' : 'ADAUGA'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
        </div>
      ) : (
        <DataTable data={categories} columns={tableColumns} pageSize={10} searchable={true} exportable={true} printable={true} selectable={true} onRowClick={handleEdit} emptyMessage="Nu exista categorii" />
      )}
    </div>
  );
};

export default EquipmentCategoriesPage;
